import React from 'react'
import type { EntryPhoto, PhotoTone } from '../types'

// ── Date helpers ──────────────────────────────────────────────────────────────
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

export function dbDate(iso: string) {
  const parts = iso.split('-').map(Number)
  const y = parts[0] ?? 0, m = parts[1] ?? 1, d = parts[2] ?? 1
  return { d, mon: MONTHS[m - 1] ?? '', y, full: `${d} ${MONTHS[m - 1] ?? ''} ${y}` }
}

// ── Photo tone palettes ───────────────────────────────────────────────────────
export const DB_TONES: Record<PhotoTone, [string, string]> = {
  warm:   ['#EAE0CF', '#D8C8AE'],
  greige: ['#E4DCCF', '#CFC4B2'],
  sky:    ['#DEE2DF', '#C6CEC9'],
  sea:    ['#D7DCD9', '#BAC4BF'],
  moss:   ['#DEDDCB', '#C3C2A9'],
}

// ── LogoMark: open book — two leaves, centre spine ───────────────────────────
export function LogoMark({
  size = 30,
  color = 'var(--db-ink)',
}: {
  size?: number
  color?: string
}) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" aria-label="Daybook" style={{ display: 'block' }}>
      <path d="M50 33 C 37 28, 21 29, 12 35 L 12 70 C 22 65, 38 65, 50 71"
        stroke={color} strokeWidth="5.5" strokeLinejoin="round" strokeLinecap="round" />
      <path d="M50 33 C 63 28, 79 29, 88 35 L 88 70 C 78 65, 62 65, 50 71"
        stroke={color} strokeWidth="5.5" strokeLinejoin="round" strokeLinecap="round" />
      <path d="M50 33 L 50 71" stroke={color} strokeWidth="5.5" strokeLinecap="round" />
    </svg>
  )
}

// ── Wordmark: "Daybook" in display serif ─────────────────────────────────────
export function Wordmark({
  size = 34,
  sub = false,
  align = 'center',
  withMark = false,
}: {
  size?: number
  sub?: boolean
  align?: React.CSSProperties['textAlign']
  withMark?: boolean
}) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: withMark ? 'column' : 'row',
      alignItems: 'center',
      justifyContent: align === 'left' ? 'flex-start' : 'center',
      gap: withMark ? size * 0.36 : 0,
      textAlign: align,
      lineHeight: 1,
    }}>
      {withMark && <LogoMark size={size * 1.1} />}
      <div>
        <div style={{
          fontFamily: 'var(--db-serif-display)',
          fontWeight: 500,
          fontSize: size,
          color: 'var(--db-ink)',
          letterSpacing: '-0.005em',
        }}>
          Daybook
        </div>
        {sub && (
          <div className="db-smallcaps" style={{
            fontFamily: 'var(--db-serif-display)',
            fontSize: size * 0.30,
            letterSpacing: '0.34em',
            color: 'var(--db-accent-ink)',
            marginTop: size * 0.22,
            marginLeft: '0.34em',
            fontWeight: 500,
          }}>
            a daybook
          </div>
        )}
      </div>
    </div>
  )
}

// ── Mono label (museum-wall-label voice) ──────────────────────────────────────
export function Mono({
  children,
  style,
}: {
  children: React.ReactNode
  style?: React.CSSProperties
}) {
  return (
    <span style={{
      fontFamily: 'var(--db-mono)',
      fontSize: 11,
      letterSpacing: '0.22em',
      textTransform: 'uppercase',
      color: 'var(--db-ink-3)',
      ...style,
    }}>
      {children}
    </span>
  )
}

// ── DateStamp — mono date + optional italic place ─────────────────────────────
export function DateStamp({ iso, place }: { iso: string; place?: string }) {
  const t = dbDate(iso)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{
        fontFamily: 'var(--db-mono)',
        fontSize: 13,
        letterSpacing: '0.26em',
        textTransform: 'uppercase',
        color: 'var(--db-ink)',
      }}>
        {t.full}
      </div>
      {place && (
        <div style={{
          fontFamily: 'var(--db-serif-body)',
          fontStyle: 'italic',
          fontSize: 16,
          color: 'var(--db-ink-2)',
        }}>
          {place}
        </div>
      )}
    </div>
  )
}

// ── SetLabel — serif small-caps chapter label ─────────────────────────────────
export function SetLabel({
  children,
  style,
}: {
  children: React.ReactNode
  style?: React.CSSProperties
}) {
  return (
    <div className="db-smallcaps" style={{
      fontFamily: 'var(--db-serif-display)',
      fontWeight: 500,
      fontSize: 13,
      letterSpacing: '0.24em',
      textTransform: 'uppercase',
      color: 'var(--db-ink-2)',
      ...style,
    }}>
      {children}
    </div>
  )
}

// ── PhotoPrint — a framed object resting on the page ─────────────────────────
export function PhotoPrint({ photo, onClick }: { photo: EntryPhoto; onClick?: () => void }) {
  const [imgFailed, setImgFailed] = React.useState(false)
  const [a, b] = DB_TONES[photo.tone] ?? DB_TONES.warm
  const showImg = photo.url && !imgFailed
  return (
    <figure style={{ margin: 0 }}>
      {showImg ? (
        // Real image: no fixed aspect ratio — display full, never cropped
        <div style={{
          width: '100%',
          border: '1px solid rgba(42,37,30,0.14)',
          boxShadow: '0 1px 1px rgba(42,37,30,0.05), 0 10px 22px -14px rgba(42,37,30,0.30)',
          overflow: 'hidden',
          lineHeight: 0,
        }}>
          <img
            src={photo.url}
            alt={photo.label}
            onError={() => setImgFailed(true)}
            onClick={onClick}
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              cursor: onClick ? 'zoom-in' : undefined,
            }}
          />
        </div>
      ) : (
        // Placeholder: keep the aspect ratio so the box looks intentional
        <div className="db-grain" style={{
          position: 'relative',
          width: '100%',
          aspectRatio: photo.ratio,
          background: `linear-gradient(150deg, ${a} 0%, ${b} 100%)`,
          border: '1px solid rgba(42,37,30,0.14)',
          boxShadow: '0 1px 1px rgba(42,37,30,0.05), 0 10px 22px -14px rgba(42,37,30,0.30)',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', padding: '0 22px', maxWidth: '82%' }}>
            <div style={{ fontFamily: 'var(--db-mono)', fontSize: 9.5, letterSpacing: '0.3em', textTransform: 'uppercase', color: 'rgba(42,37,30,0.34)', marginBottom: 8 }}>
              {photo.video ? 'Video' : 'Photograph'}
            </div>
            <div style={{ fontFamily: 'var(--db-serif-body)', fontStyle: 'italic', fontSize: 14.5, lineHeight: 1.4, color: 'rgba(42,37,30,0.46)' }}>
              {photo.label}
            </div>
          </div>
        </div>
      )}
    </figure>
  )
}

// ── Lightbox — tap to dismiss fullscreen image viewer ────────────────────────
export function Lightbox({ url, alt, onClose }: { url: string; alt?: string; onClose: () => void }) {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.92)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'zoom-out',
      }}
    >
      <img
        src={url}
        alt={alt ?? ''}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          display: 'block',
          cursor: 'default',
        }}
      />
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          width: 36,
          height: 36,
          borderRadius: 999,
          background: 'rgba(255,255,255,0.12)',
          border: 'none',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 14 14"><path d="M2 2l10 10M12 2L2 12" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/></svg>
      </button>
    </div>
  )
}

// ── Caption ───────────────────────────────────────────────────────────────────
export function Caption({ children }: { children: React.ReactNode }) {
  return (
    <figcaption style={{
      fontFamily: 'var(--db-serif-body)',
      fontSize: 15,
      fontStyle: 'italic',
      color: 'var(--db-ink-2)',
      marginTop: 10,
      lineHeight: 1.45,
    }}>
      {children}
    </figcaption>
  )
}

// ── Rule ──────────────────────────────────────────────────────────────────────
export function Rule({ style }: { style?: React.CSSProperties }) {
  return <div style={{ height: 1, background: 'var(--db-line)', ...style }} />
}

// ── Icon set (stroked, quiet) ─────────────────────────────────────────────────
export const DBIcon = {
  back:   (c = 'var(--db-ink)') => <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12 4l-6 6 6 6" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  key:    (c = 'var(--db-ink-2)') => <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><circle cx="7" cy="7" r="3.4" stroke={c} strokeWidth="1.4"/><path d="M9.4 9.4l6 6M13 13l1.6-1.6M15 15l1.4-1.4" stroke={c} strokeWidth="1.4" strokeLinecap="round"/></svg>,
  plus:   (c = 'var(--db-ink)') => <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 4v12M4 10h12" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg>,
  home:   (c = 'var(--db-ink-2)') => <svg width="20" height="20" viewBox="0 0 22 22" fill="none"><path d="M4 9.5L11 4l7 5.5V18a1 1 0 01-1 1h-3v-5h-6v5H5a1 1 0 01-1-1V9.5z" stroke={c} strokeWidth="1.4" strokeLinejoin="round"/></svg>,
  book:   (c = 'var(--db-ink-2)') => <svg width="20" height="20" viewBox="0 0 22 22" fill="none"><path d="M11 5.5C9.5 4.3 7.3 4 5 4.2V17c2.3-.2 4.5 0 6 1.2 1.5-1.2 3.7-1.4 6-1.2V4.2c-2.3-.2-4.5.1-6 1.3zM11 5.5V18" stroke={c} strokeWidth="1.3" strokeLinejoin="round"/></svg>,
  camera: (c = 'var(--db-ink-2)') => <svg width="20" height="20" viewBox="0 0 22 22" fill="none"><rect x="3" y="6.5" width="16" height="11" rx="2" stroke={c} strokeWidth="1.3"/><circle cx="11" cy="12" r="3" stroke={c} strokeWidth="1.3"/><path d="M8 6.5l1-2h4l1 2" stroke={c} strokeWidth="1.3" strokeLinejoin="round"/></svg>,
  video:  (c = 'var(--db-ink-2)') => <svg width="20" height="20" viewBox="0 0 22 22" fill="none"><rect x="3" y="6" width="11" height="10" rx="2" stroke={c} strokeWidth="1.3"/><path d="M14 9.5l5-2.5v8l-5-2.5" stroke={c} strokeWidth="1.3" strokeLinejoin="round"/></svg>,
  pin:    (c = 'var(--db-ink-3)') => <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M7 1.5C4.8 1.5 3 3.2 3 5.4 3 8.3 7 12.5 7 12.5s4-4.2 4-7.1C11 3.2 9.2 1.5 7 1.5z" stroke={c} strokeWidth="1.2"/><circle cx="7" cy="5.3" r="1.3" stroke={c} strokeWidth="1.2"/></svg>,
  check:  (c = '#fff') => <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 7.5l2.5 2.5L11 4" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  chevron:(c = 'var(--db-ink-3)') => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M5 3l5 5-5 5" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
}

// ── RoundBtn ──────────────────────────────────────────────────────────────────
export function RoundBtn({
  onClick,
  children,
  title,
}: {
  onClick?: () => void
  children: React.ReactNode
  title?: string
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        width: 40,
        height: 40,
        borderRadius: 999,
        border: '1px solid var(--db-line)',
        background: 'var(--db-paper-lift)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        transition: 'background 0.2s, border-color 0.2s',
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--db-paper-sink)' }}
      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--db-paper-lift)' }}
    >
      {children}
    </button>
  )
}

// ── TopBar ────────────────────────────────────────────────────────────────────
export function TopBar({
  left,
  center,
  right,
}: {
  left?: React.ReactNode
  center?: React.ReactNode
  right?: React.ReactNode
}) {
  return (
    <div style={{
      position: 'relative',
      zIndex: 8,
      flexShrink: 0,
      background: 'var(--db-paper)',
      borderBottom: '1px solid var(--db-line-soft)',
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '44px 1fr 44px',
        alignItems: 'center',
        height: 56,
        padding: '0 10px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>{left}</div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{center}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{right}</div>
      </div>
    </div>
  )
}

// ── Screen shell ──────────────────────────────────────────────────────────────
export function Screen({ children }: { children: React.ReactNode }) {
  return (
    <div className="db-page" style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {children}
    </div>
  )
}

// ── Shared button / input styles ──────────────────────────────────────────────
export const inputCss: React.CSSProperties = {
  width: '100%',
  border: 'none',
  borderBottom: '1px solid var(--db-line)',
  background: 'transparent',
  fontFamily: 'var(--db-serif-body)',
  fontSize: 17,
  color: 'var(--db-ink)',
  padding: '8px 0',
  outline: 'none',
}

export const solidBtn: React.CSSProperties = {
  background: 'var(--db-ink)',
  color: 'var(--db-paper)',
  border: 'none',
  borderRadius: 999,
  padding: '9px 18px',
  fontFamily: 'var(--db-mono)',
  fontSize: 10.5,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  cursor: 'pointer',
}

export const ghostBtn: React.CSSProperties = {
  background: 'none',
  color: 'var(--db-ink-2)',
  border: '1px solid var(--db-line)',
  borderRadius: 999,
  padding: '9px 18px',
  fontFamily: 'var(--db-mono)',
  fontSize: 10.5,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  cursor: 'pointer',
}
